// Workspace → Calendar: the org's month at a glance, and what is coming up.
//
// Holidays come from public.holidays and are only ever edited in Org Settings.
// Non-working days are marked from org_settings.workdays alone — a holiday already
// has its own chip, so handing isWorkday the holiday set would say it twice.
// "Today" is the ORG's today, never the browser's (docs/UI-CONVENTIONS.md §2).
//
// Events (lib/calendarEvents.ts) arrive already cut down by RLS to what this person
// may see; the audience filter here only narrows that further, and the New event /
// Edit / Delete controls at calendar:full are UX — the RPCs refuse anyone else.
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Checkbox,
  Icon,
  Select,
  TONE,
  markColor,
  type IconName,
} from './primitives';
import { ConfirmDialog, Dialog } from './dialog';
import { Card, Eyebrow, PeriodStepper, SegmentedControl } from './card';
import { DateField, Field, TextArea, TextInput } from './field';
import { EmptyState, InlineStatus, LoadError, useToast } from './feedback';
import { DayMark, DayMarkKey, MonthGrid, dayMarkStyle } from './monthGrid';
import {
  addDaysToKey,
  fmtDate,
  fmtDayKey,
  fmtMonth,
  monthAbbr,
  weekdayAbbr,
  isoDay,
  monthRange,
  orgDateKey,
  orgTime,
  orgToday,
  toOrgWallTime,
  tzLabel,
} from '../lib/dates';
import { useBreakpoint, useMediaQuery } from '../lib/useMediaQuery';
import { useOrgDates, type EventType } from '../lib/orgSettings';
import { isWorkday, workingDays } from '../lib/workdays';
import { HOLIDAY_KINDS, listHolidays, type Holiday, type HolidayKind } from '../lib/holidays';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { can } from '../lib/roles';
import { departmentName, useDepartments, type Department } from '../lib/departments';
import {
  deleteEvent,
  eventDays,
  eventSpan,
  eventTypeLabel,
  listEvents,
  pickableTypes,
  saveEvent,
  type Audience,
  type CalendarEvent,
  type EventInput,
  type EventTimes,
} from '../lib/calendarEvents';
import { linkCreatedAt, resetLink } from '../lib/calendarLinks';
import { notInGoogle } from '../lib/googleImports';

// The categorical palette for event types (WS-09). A type's colour is its place
// in policy.calendar.types, archived included, so archiving one never repaints
// the rest — which is why this stays EIGHT slots: the index is `i % 8`, and a
// shorter list would repaint every type an org already has.
//
// A category never borrows a meaning (UI-CONVENTIONS §3). The first four slots
// used to be amber ("waiting on someone"), sky, the regional-holiday coral and
// rose ("wrong"): a Training session read as a regional holiday and a Deadline
// as an error. Nothing here is sage, amber, rose or the accent, nor one of the
// four HOLIDAY_KINDS hues: the hues that mean something on a calendar. Slots 1
// and 4–7 are the colours those places already had. Checked with the dataviz
// palette validator: every neighbouring pair is apart under protan and deutan
// simulation; tan and slate, the two incumbents, are the closest pair.
//
// The slots are the --hf-cat-1…8 tokens (tokens.css), which give each a light
// value: as hexes here, several were under 3:1 as a bar on the light card, and
// the pale yellow all but vanished. They are fills and a 3px bar only, never
// text — the chip's words stay ink, and every event names its type in text, so
// the colour is never the only cue. tokens.test.ts holds each at 3:1 in both
// themes and clear of every hue that means something here.
export const EVENT_TYPE_COLORS = [
  'var(--hf-cat-1)', // olive
  'var(--hf-cat-2)', // cyan
  'var(--hf-cat-3)', // plum
  'var(--hf-cat-4)', // teal
  'var(--hf-cat-5)', // yellow
  'var(--hf-cat-6)', // pink
  'var(--hf-cat-7)', // tan
  'var(--hf-cat-8)', // slate
] as const;
const typeColor = (types: readonly EventType[], id: string): string => {
  const i = types.findIndex((t) => t.id === id);
  return i < 0 ? 'var(--hf-ink-3)' : EVENT_TYPE_COLORS[i % EVENT_TYPE_COLORS.length];
};
/** `pct`% of a colour over whatever is behind it. Works for a hex or a var(). */
const tint = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

const OFF_DAY = 'repeating-linear-gradient(135deg, var(--hf-surface-3) 0 4px, transparent 4px 8px)';

// Optional holidays read as a dashed outline, the other kinds as a fill: shape,
// not only hue. The hue comes from the kind's tone, so it has a light-theme value.
const kindLook = (kind: HolidayKind): CSSProperties => {
  const t = TONE[HOLIDAY_KINDS[kind].tone];
  return kind === 'optional'
    ? { background: 'transparent', border: `1px dashed ${t.fg}` }
    : { background: t.bg, border: `1px solid ${t.bd}` };
};

// Events carry a bar on the left and keep ink text, so a pale type stays legible.
const eventLook = (color: string): CSSProperties => ({
  background: tint(color, 15),
  border: `1px solid ${tint(color, 33)}`,
  borderLeft: `3px solid ${color}`,
});

const swatch: CSSProperties = {
  width: 'var(--hf-space-2)',
  height: 'var(--hf-space-2)',
  borderRadius: 'var(--hf-radius-xs)',
  flexShrink: 0,
};

// Below 768px a day cell holds marks, not chips, and the mark is the only thing
// a sighted person sees of the day: a mark under 3:1 is no mark (WCAG 1.4.11,
// and markColor in primitives). The chip looks above are washes that lean on
// their words, and as an 8px square they measured 1.2–2.2:1. So a holiday and
// a non-working day take the shared day marks (dayMarkStyle, monthGrid.tsx):
// a solid square in the kind's text-safe hue, the dashed square for optional,
// and thin stripes with no box — the marks /me and a person's record draw, so
// Diwali is one shape on every calendar (critique 2026-10-05 #1). An event is
// not a day mark: it keeps its type's colour and takes the control edge.
const eventMark = (color: string): CSSProperties => ({
  background: color,
  border: '1px solid var(--hf-rule-control)',
});

// A chip in a day cell: the densest surface the calendar has, so the smallest
// step, the 11px floor (UI-REMEDIATION-PLAN §8.1, decided 2026-10-08). It was
// 10.5px.
const chip: CSSProperties = {
  display: 'block',
  // MonthGrid lays a cell's items in a wrapping column, where an item is as wide
  // as its longest word-run: a long title pushed its chip past the cell's edge
  // and was cut there, with no ellipsis. Contained, the chip asks for no width
  // and is stretched to the cell's, so the ellipsis below does the cutting.
  contain: 'inline-size',
  padding: '0 var(--hf-space-1)',
  borderRadius: 'var(--hf-radius-sm)',
  fontSize: 'var(--hf-text-2xs)',
  lineHeight: 'var(--hf-leading-body)',
  color: 'var(--hf-ink)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// A holiday's kind beside its name, in Upcoming and a day's list: the shared
// day mark and its words, at the key's size. It was a "• Public" pill there, a
// third look for a kind the grid draws as a square (critique 2026-10-06).
const kindMark: CSSProperties = {
  flexShrink: 0,
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
};

const rowLook: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--hf-space-3)' };
// The line under an Upcoming or a day's row: "Meeting · 10:00–11:00", "Tue".
// The UI face, figures aligned: it was mono, which made the event type's name
// read as code (critique 2026-10-06, mono carrying prose).
const rowMeta: CSSProperties = {
  display: 'block',
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
  fontVariantNumeric: 'tabular-nums',
};
const note: CSSProperties = {
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-2)',
  lineHeight: 'var(--hf-leading-body)',
};
const stack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-4)',
};
const plainList: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

// The one wording for an audience: the form's choices (in this order), the filter
// and the event detail, which names the departments instead of the last one.
export const AUDIENCE_LABEL: Record<Audience, string> = {
  company: 'Company-wide',
  admin: 'Admin only',
  department: 'Department(s)',
};

/** "Fri 8 Jan", and "Fri 8 Jan 2027" once it is not the org's current year: the
 *  month stepper and the Google imports list both cross years, and nothing else
 *  on an event's detail, a day's dialog or an import's row says which. */
const dayLabel = (key: string, tz: string) => {
  const label = fmtDayKey(key, { weekday: true });
  const year = key.slice(0, 4);
  return year === orgDateKey(Date.now(), tz).slice(0, 4) ? label : `${label} ${year}`;
};
/** "09:30", the org's clock without AM/PM: a day cell has no room for more. */
const shortTime = (iso: string, tz: string) => toOrgWallTime(iso, tz).slice(11);

/** When an event happens, on the org's calendar and clock. `omitDate` leaves the
 *  day out of a one-day event, for a row that already shows it (WS-16: Upcoming
 *  repeated the date its tile showed and cut off the time). A span of several
 *  days always names both ends. */
export const whenText = (e: EventTimes, tz: string, opts: { omitDate?: boolean } = {}): string => {
  const [a, b] = eventSpan(e, tz);
  const oneDay = a === b;
  if (e.all_day) {
    if (!oneDay) return `${dayLabel(a, tz)} – ${dayLabel(b, tz)} · All day`;
    return opts.omitDate ? 'All day' : `${dayLabel(a, tz)} · All day`;
  }
  const s = orgTime(e.starts_at ?? '', tz);
  const end = orgTime(e.ends_at ?? '', tz);
  if (!oneDay) return `${dayLabel(a, tz)}, ${s} – ${dayLabel(b, tz)}, ${end}`;
  return opts.omitDate ? `${s}–${end}` : `${dayLabel(a, tz)} · ${s}–${end}`;
};

// A <span>: it sits inside EventRow's <button>, which takes phrasing content only.
const DateTile = ({ day, look }: { day: string; look: CSSProperties }) => {
  // The key's own fields: split out of fmtDayKey's label, a change to its shape
  // would have put the month in the day's slot.
  const date = Number(day.slice(8));
  const month = monthAbbr(day);
  return (
    <span
      style={{
        ...look,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: 42,
        padding: 'var(--hf-space-1) 0',
        borderRadius: 'var(--hf-radius-md)',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--hf-font-display)',
          fontSize: 'var(--hf-text-lg)',
          color: 'var(--hf-ink)',
          lineHeight: 'var(--hf-leading-none)',
        }}
      >
        {date}
      </span>
      {/* ink-3, not the tile's hue: a pale type's month was unreadable (WS-02). */}
      <span
        style={{
          fontSize: 'var(--hf-text-2xs)',
          fontFamily: 'var(--hf-font-mono)',
          color: 'var(--hf-ink-3)',
          textTransform: 'uppercase',
          marginTop: 'var(--hf-space-1)',
        }}
      >
        {month}
      </span>
    </span>
  );
};

type Loaded<T> = { data: T[]; error: { message: string } | null };
const NOTHING = { data: [], error: null };

/** The event form's subject: an event to edit, or a new one (on `day`, if given). */
type FormTarget = { existing: CalendarEvent } | { day: string | null };

type MyLeave = {
  id: string;
  start_date: string;
  end_date: string;
  status: 'pending' | 'approved';
  leave_types: { label: string; color: string | null } | null;
};

export function CalendarTab() {
  const { weekStartsOn, tz, dateFormat, workdays, eventTypes, calendarLinks } = useOrgDates();
  const { perms, profile } = useAuth();
  const departments = useDepartments();
  const toast = useToast();
  // Below 768px a day cell is ~45px wide: chips there were one letter each
  // (WS-05), so the cell shows a mark per item and the day opens the list.
  const bp = useBreakpoint();
  const compact = bp === 'sm' || bp === 'md';
  // A finger, at any width: the Show chips grow to 44px (UI-CONVENTIONS §8).
  const coarse = useMediaQuery('(pointer: coarse)');
  const canPost = can(perms, 'calendar', 'full');
  const seesAll = can(perms, 'calendar', 'edit');
  const today = orgToday(tz);
  const todayKey = isoDay(today);

  // ?date=YYYY-MM-DD comes from a notification link: open that month and ring the day.
  const [params] = useSearchParams();
  const dateParam = params.get('date') ?? '';
  const focus = /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : null;
  const focusOffset = focus
    ? (Number(focus.slice(0, 4)) - today.getFullYear()) * 12 +
      (Number(focus.slice(5, 7)) - 1 - today.getMonth())
    : null;

  // Months away from the org's current month — an offset, not a stored year/month,
  // so the grid follows `tz` when the org settings arrive after the first render.
  const [offset, setOffset] = useState(focusOffset ?? 0);
  // Follow a second notification link while the tab is already open.
  useEffect(() => {
    if (focusOffset !== null) setOffset(focusOffset);
  }, [focusOffset]);
  const first = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const y = first.getFullYear();
  const m = first.getMonth();
  const { from, to } = monthRange(first);
  const horizon = monthRange(today, 6).to;

  // Hidden categories, keyed `type:<id>` or 'holidays' — no type id can collide.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const showHolidays = !hidden.has('holidays');
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  // 'all' | 'company' | 'admin' | a department id.
  const [audience, setAudience] = useState('all');

  // Bumped after a save or delete, and by Retry, so every list refetches.
  const [rev, setRev] = useState(0);
  const retry = () => setRev((r) => r + 1);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState<FormTarget | null>(null);
  const [subscribing, setSubscribing] = useState(false);

  const [monthHols, setMonthHols] = useState<Loaded<Holiday>>(NOTHING);
  useEffect(() => {
    let live = true;
    void listHolidays(from, to).then((h) => live && setMonthHols(h));
    return () => {
      live = false;
    };
  }, [from, to, rev]);

  // Your own leave on your calendar (owner, 2026-10-09: "on the workspace
  // calendar people see only their own leaves"). lr_read already lets a person
  // read their own requests; nobody else's leave is read here.
  const me = profile?.id;
  const [myLeave, setMyLeave] = useState<MyLeave[]>([]);
  useEffect(() => {
    if (!supabase || !me) return;
    let live = true;
    void supabase
      .from('leave_requests')
      .select('id, start_date, end_date, status, leave_types(label, color)')
      .eq('profile_id', me)
      .in('status', ['pending', 'approved'])
      .lte('start_date', to)
      .gte('end_date', from)
      .then(({ data }) => live && setMyLeave((data as unknown as MyLeave[]) ?? []));
    return () => {
      live = false;
    };
  }, [me, from, to, rev]);

  const [monthEvents, setMonthEvents] = useState<Loaded<CalendarEvent>>(NOTHING);
  useEffect(() => {
    let live = true;
    void listEvents(from, to, tz).then((e) => live && setMonthEvents(e));
    return () => {
      live = false;
    };
  }, [from, to, tz, rev]);

  // Events whose push to Google keeps failing — only calendar:full can read the
  // log (calendar_sync_failures), and only they are told. Asked when an event is
  // opened: the push runs after the save commits, so asking on save is too early.
  // null until asked. A failed check is kept as one: "not marked" would say the
  // event reached Google when nobody knows (UI-CONVENTIONS §7).
  const [push, setPush] = useState<Awaited<ReturnType<typeof notInGoogle>> | null>(null);
  const [pushTry, setPushTry] = useState(0);
  const openId = openEvent?.id;
  useEffect(() => {
    if (!canPost || !openId) return;
    let live = true;
    void notInGoogle().then((res) => live && setPush(res));
    return () => {
      live = false;
    };
  }, [canPost, openId, pushTry]);

  const [upHols, setUpHols] = useState<Loaded<Holiday> | null>(null);
  useEffect(() => {
    let live = true;
    void listHolidays(todayKey, horizon).then((h) => live && setUpHols(h));
    return () => {
      live = false;
    };
  }, [todayKey, horizon, rev]);

  const [upEvents, setUpEvents] = useState<Loaded<CalendarEvent> | null>(null);
  useEffect(() => {
    let live = true;
    void listEvents(todayKey, horizon, tz).then((e) => live && setUpEvents(e));
    return () => {
      live = false;
    };
  }, [todayKey, horizon, tz, rev]);

  // A read that failed is said, with a Retry; it is never drawn as an empty
  // month or "nothing coming up" (WS-V1, UI-CONVENTIONS §7).
  const monthFailed = !!(monthHols.error ?? monthEvents.error);
  const upFailed = !!(upHols?.error ?? upEvents?.error);

  // Archived types drop out of the filter unless something on screen still uses them.
  const onScreen = [...monthEvents.data, ...(upEvents?.data ?? [])];
  const inView = new Set(onScreen.map((e) => e.type));
  const shownTypes = eventTypes.filter((t) => !t.archived || inView.has(t.id));
  const categories = [
    ...shownTypes.map((t) => ({
      key: `type:${t.id}`,
      label: t.label,
      color: typeColor(eventTypes, t.id),
    })),
    { key: 'holidays', label: 'Holidays', color: markColor(HOLIDAY_KINDS.public.tone) },
    { key: 'myleave', label: 'My leave', color: 'var(--hf-ink-3)' },
  ];

  // Mirrors RLS for the choices only: at calendar:view a person sees company-wide
  // events and their own department's, so offering another department is pointless.
  const targeted = new Set(onScreen.flatMap((e) => e.department_ids));
  const audienceOptions = [
    { value: 'all', label: 'All audiences' },
    { value: 'company', label: AUDIENCE_LABEL.company },
    ...(seesAll ? [{ value: 'admin', label: AUDIENCE_LABEL.admin }] : []),
    ...departments
      .filter((d) =>
        seesAll ? !d.archived || targeted.has(d.id) : d.id === profile?.department_id,
      )
      .map((d) => ({ value: d.id, label: d.name })),
  ];

  const shows = (e: CalendarEvent) =>
    !hidden.has(`type:${e.type}`) &&
    (audience === 'all' ||
      e.audience === audience ||
      (e.audience === 'department' && e.department_ids.includes(audience)));

  const byDate = new Map<string, Holiday[]>();
  if (showHolidays)
    for (const h of monthHols.data) byDate.set(h.date, [...(byDate.get(h.date) ?? []), h]);
  const showMine = !hidden.has('myleave');
  const leaveByDate = new Map<string, MyLeave[]>();
  if (showMine)
    for (const l of myLeave)
      for (
        let d = l.start_date < from ? from : l.start_date;
        d <= l.end_date && d <= to;
        d = addDaysToKey(d, 1)
      )
        leaveByDate.set(d, [...(leaveByDate.get(d) ?? []), l]);
  const eventsByDate = new Map<string, CalendarEvent[]>();
  for (const e of monthEvents.data.filter(shows))
    for (const d of eventDays(e, tz)) eventsByDate.set(d, [...(eventsByDate.get(d) ?? []), e]);

  const loaded = upHols !== null && upEvents !== null;
  // Upcoming leaves out timed events already over, and files one still running
  // under today rather than the past day it started.
  const now = Date.now();
  const rows: ({ day: string } & ({ hol: Holiday } | { ev: CalendarEvent }))[] = [
    ...(showHolidays ? (upHols?.data ?? []) : []).map((hol) => ({ day: hol.date, hol })),
    ...(upEvents?.data ?? [])
      .filter((ev) => shows(ev) && (ev.all_day || new Date(ev.ends_at ?? 0).getTime() > now))
      .map((ev) => {
        const start = eventSpan(ev, tz)[0];
        return { day: start > todayKey ? start : todayKey, ev };
      }),
  ]
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(0, 8);

  // A save or delete closes its dialog; the receipt is a toast on the page it
  // returns to (a toast under an open modal could not be read or clicked).
  const done = (receipt: string) => {
    setForm(null);
    setOpenEvent(null);
    retry();
    toast.push({ tone: 'success', message: receipt });
  };

  // What a day cell holds. The cell is one button (MonthGrid) and these are its
  // description, read after the date: so every item says what it is in words —
  // the holiday's kind, the event's type — and colour is never the only cue
  // (WS-09, WS-13). In `compact` each is a mark and the words are for a screen
  // reader; the day's dialog lists them in full.
  const renderDay = (key: string): ReactNode[] => {
    const items: ReactNode[] = [];
    // Your leave tints the whole cell (tintOf on the grid, as on My Space);
    // this says it to a screen reader.
    for (const l of leaveByDate.get(key) ?? [])
      items.push(
        <span key={`l${l.id}`} className="hf-sr-only">
          Your {l.leave_types?.label ?? 'Leave'} leave
          {l.status === 'pending' ? ', waiting for HR' : ''}
        </span>,
      );
    for (const h of byDate.get(key) ?? []) {
      const kind = `${HOLIDAY_KINDS[h.kind].label} holiday`;
      items.push(
        compact ? (
          <span key={h.id} style={{ ...dayMarkStyle(h.kind), display: 'block' }}>
            <span className="hf-sr-only">
              {h.name}, {kind}
            </span>
          </span>
        ) : (
          <span key={h.id} style={{ ...chip, ...kindLook(h.kind) }}>
            {h.name}
            {h.kind === 'optional' && <span aria-hidden="true"> · Optional</span>}
            <span className="hf-sr-only">, {kind}</span>
          </span>
        ),
      );
    }
    for (const e of eventsByDate.get(key) ?? []) {
      const color = typeColor(eventTypes, e.type);
      // The time only on the day it starts; later days of a long event are just the title.
      const time =
        !e.all_day && e.starts_at && orgDateKey(e.starts_at, tz) === key
          ? shortTime(e.starts_at, tz)
          : null;
      const type = eventTypeLabel(eventTypes, e.type);
      items.push(
        compact ? (
          <span key={e.id} style={{ ...swatch, ...eventMark(color), display: 'block' }}>
            <span className="hf-sr-only">
              {time} {e.title}, {type}
            </span>
          </span>
        ) : (
          <span key={e.id} style={{ ...chip, ...eventLook(color) }}>
            {time && (
              <span style={{ fontFamily: 'var(--hf-font-mono)', color: 'var(--hf-ink-2)' }}>
                {time}{' '}
              </span>
            )}
            {e.title}
            <span className="hf-sr-only">, {type}</span>
          </span>
        ),
      );
    }
    // MonthGrid owns the cell and has no per-day background, so a non-working
    // day's hatch rides in the cell's content. It is not an item of its own:
    // MonthGrid counts items against maxPerDay, and a Saturday with three
    // events showed two and "+1 more" where a weekday showed all three. So it
    // shares the first item's slot, or is the only thing in an empty day.
    if (!isWorkday(key, workdays)) {
      const hatch = (
        <span
          style={
            compact
              ? { ...dayMarkStyle('off'), display: 'block' }
              : // The cell's width of the same stripes, quieter: at the mark's
                // ink, ten of them a month were the loudest thing on the grid.
                {
                  display: 'block',
                  height: 'var(--hf-space-2)',
                  minWidth: 'var(--hf-space-2)',
                  borderRadius: 'var(--hf-radius-xs)',
                  background: OFF_DAY,
                  border: '1px solid var(--hf-rule-2)',
                }
          }
        >
          <span className="hf-sr-only">Non-working day</span>
        </span>
      );
      items[0] = items.length ? (
        <span
          style={{
            display: 'flex',
            // As MonthGrid lays a cell's items: a row of marks, or a column of chips.
            flexDirection: compact ? 'row' : 'column',
            gap: 'var(--hf-space-1)',
            minWidth: 0,
          }}
        >
          {hatch}
          {items[0]}
        </span>
      ) : (
        hatch
      );
    }
    return items;
  };

  return (
    <div style={stack}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div
          role="group"
          aria-label="Categories"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
            // Below md the chips are one row that scrolls sideways, as Tabs
            // does: wrapped, eight of them were three rows above the month at
            // 390px. It takes the row's spare width before the Audience Select
            // does, and has the row to itself once that is too narrow for both.
            // The padding, given back by the margin, is room for a chip's focus
            // ring, which the scroller would otherwise clip.
            ...(compact
              ? {
                  flex: '999 1 16rem',
                  minWidth: 0,
                  overflowX: 'auto',
                  padding: 'var(--hf-space-1)',
                  margin: 'calc(-1 * var(--hf-space-1))',
                  // The edge that can still scroll fades, as .hf-table-scroll's
                  // does: at 320 the row broke between two chips and read as
                  // the whole list. `animationTimeline` after `animation`,
                  // which resets it. No scroll timeline: no fade, nothing worse.
                  maskImage:
                    'linear-gradient(to right, black calc(100% - var(--hf-fade-end)), transparent)',
                  animation: 'hf-fade-end linear both',
                  animationTimeline: 'scroll(self inline)',
                }
              : { flexWrap: 'wrap' }),
          }}
        >
          <Eyebrow style={{ flexShrink: 0 }}>Show</Eyebrow>
          {categories.map((c) => {
            const on = !hidden.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(c.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2)',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  // Grown for real, not by .hf-hit: a scroller clips a hit area
                  // that reaches past it.
                  minHeight: coarse ? 44 : undefined,
                  padding: 'var(--hf-space-1) var(--hf-space-3)',
                  borderRadius: 'var(--hf-radius-pill)',
                  fontSize: 'var(--hf-text-sm)',
                  fontFamily: 'var(--hf-font-ui)',
                  cursor: 'pointer',
                  background: on ? 'var(--hf-surface-3)' : 'transparent',
                  // The edge is how an off chip is seen at all: rule-control.
                  border: '1px solid var(--hf-rule-control)',
                  color: on ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                }}
              >
                {/* Filled when shown, hollow when hidden: shape as well as colour. */}
                <span
                  style={{
                    ...swatch,
                    background: on ? c.color : 'transparent',
                    border: `1px solid ${c.color}`,
                  }}
                />
                {c.label}
              </button>
            );
          })}
        </div>
        {!compact && <div style={{ flex: 1 }} />}
        <div style={compact ? { flex: '1 0 170px' } : { minWidth: 170 }}>
          <Select
            ariaLabel="Audience"
            value={audience}
            options={audienceOptions}
            onChange={setAudience}
            align="right"
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-4)',
          alignItems: 'flex-start',
        }}
      >
        <Card style={{ flex: '3 1 560px' }}>
          {/* Wraps: at 390px the next-month arrow and New event were pushed off
              the card's edge (WS-05). Wrapped, the lines are 16px apart: at 8
              the 44px areas of the arrows and of the buttons under them (6px
              and 7px of reach) overlapped, and a tap just under Next month
              opened the form. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 'var(--hf-space-2)',
              rowGap: 'var(--hf-space-4)',
              flexWrap: 'wrap',
              marginBottom: 'var(--hf-space-3)',
            }}
          >
            {/* On a phone the stepper draws Today only while away from this
                month, and wraps it under the arrows when short of room. */}
            <PeriodStepper
              label={fmtMonth(y, m)}
              unit="month"
              onPrev={() => setOffset((o) => o - 1)}
              onNext={() => setOffset((o) => o + 1)}
              onReset={offset === 0 ? undefined : () => setOffset(0)}
              resetLabel="Today"
            />
            {/* Pushed to the right edge, on the stepper's line or, wrapped, on
                their own: on a phone that is where a right thumb is. */}
            <div style={{ display: 'flex', gap: 'var(--hf-space-2)', marginLeft: 'auto' }}>
              <Button size="sm" icon="link" className="hf-hit" onClick={() => setSubscribing(true)}>
                Subscribe
              </Button>
              {canPost && (
                <Button
                  size="sm"
                  variant="primary"
                  icon="plus"
                  className="hf-hit"
                  onClick={() => setForm({ day: null })}
                >
                  New event
                </Button>
              )}
            </div>
          </div>

          {monthFailed && (
            <div style={{ marginBottom: 'var(--hf-space-3)' }}>
              <LoadError what="this month’s calendar" onRetry={retry} />
            </div>
          )}

          <MonthGrid
            year={y}
            month={m}
            weekStartsOn={weekStartsOn}
            label={fmtMonth(y, m)}
            today={todayKey}
            selectedKey={focus ?? undefined}
            compact={compact}
            maxPerDay={compact ? 6 : 3}
            renderDay={renderDay}
            onSelectDay={setOpenDay}
            // Your leave day is the cell, in its type's colour, as on My Space.
            tintOf={(key) => {
              const l = leaveByDate.get(key)?.[0];
              return l
                ? {
                    color: l.leave_types?.color ?? 'var(--hf-ink-3)',
                    outlined: l.status === 'pending',
                  }
                : null;
            }}
          />

          {/* Only what the Show chips don't already key: a holiday's kind and
              the hatch of a non-working day. The event types were listed here a
              second time (WS-18). The keys are the shared day marks, as /me and
              a person's record key theirs: a wash swatch was 1.2–2.2:1 in light.
              Only what this month draws: it keyed all four kinds and the hatch
              whatever the month held (critique 2026-10-06 #2). */}
          <DayMarkKey
            holidays={showHolidays ? monthHols.data : []}
            off={workingDays(from, to, workdays) < Number(to.slice(8))}
            // Your own leave (owner, 2026-10-09), keyed only when the month draws it.
            leaveTypes={
              showMine
                ? [
                    ...new Map(
                      myLeave.map((l) => [
                        l.leave_types?.label ?? 'Leave',
                        {
                          label: l.leave_types?.label ?? 'Leave',
                          color: l.leave_types?.color ?? undefined,
                        },
                      ]),
                    ).values(),
                  ]
                : []
            }
            pending={showMine && myLeave.some((l) => l.status === 'pending')}
          />
        </Card>

        <Card label="Upcoming" style={{ flex: '1 1 260px' }}>
          {upFailed ? (
            <LoadError what="what’s coming up" onRetry={retry} />
          ) : (
            loaded &&
            !rows.length &&
            (hidden.size || audience !== 'all' ? (
              <EmptyState title="Nothing matches these filters" />
            ) : (
              <EmptyState title="Nothing in the next six months">
                Holidays and the events you can see appear here as they are added.
              </EmptyState>
            ))
          )}
          {!upFailed && (
            <ul style={plainList}>
              {rows.map((r, i) => (
                <li
                  key={'hol' in r ? `h${r.hol.id}` : `e${r.ev.id}`}
                  style={{
                    padding: 'var(--hf-space-2) 0',
                    borderTop: i ? '1px solid var(--hf-rule)' : undefined,
                  }}
                >
                  {'hol' in r ? (
                    <div style={rowLook}>
                      <DateTile day={r.day} look={kindLook(r.hol.kind)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                          {r.hol.name}
                        </div>
                        {/* The weekday alone: the date tile beside it says the day and month. */}
                        <span style={rowMeta}>{weekdayAbbr(r.day)}</span>
                      </div>
                      <span style={kindMark}>
                        <DayMark kind={r.hol.kind} />
                      </span>
                    </div>
                  ) : (
                    <EventRow
                      event={r.ev}
                      day={r.day}
                      types={eventTypes}
                      tz={tz}
                      onOpen={() => setOpenEvent(r.ev)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {openDay && (
        <DayDialog
          day={openDay}
          off={!isWorkday(openDay, workdays)}
          failed={monthFailed}
          holidays={byDate.get(openDay) ?? []}
          events={eventsByDate.get(openDay) ?? []}
          types={eventTypes}
          tz={tz}
          canPost={canPost}
          onClose={() => setOpenDay(null)}
          onOpenEvent={(e) => {
            setOpenDay(null);
            setOpenEvent(e);
          }}
          onNew={() => {
            setForm({ day: openDay });
            setOpenDay(null);
          }}
        />
      )}
      {openEvent && (
        <EventDetail
          event={openEvent}
          types={eventTypes}
          tz={tz}
          departments={departments}
          canPost={canPost}
          // A Google event is never pushed back, so there is nothing to mark.
          google={
            !push || openEvent.source === 'google'
              ? undefined
              : push.error
                ? 'unknown'
                : push.ids.has(openEvent.id)
                  ? 'pending'
                  : undefined
          }
          onRetryGoogle={() => setPushTry((n) => n + 1)}
          onClose={() => setOpenEvent(null)}
          onEdit={() => {
            setForm({ existing: openEvent });
            setOpenEvent(null);
          }}
          onDeleted={() => done(`Deleted “${openEvent.title}”. Its audience has been told.`)}
        />
      )}
      {form && (
        <EventForm
          existing={'existing' in form ? form.existing : null}
          day={'day' in form ? form.day : null}
          types={eventTypes}
          tz={tz}
          departments={departments}
          onClose={() => setForm(null)}
          onSaved={(title) => done('existing' in form ? `Saved “${title}”.` : `Posted “${title}”.`)}
        />
      )}
      {subscribing && (
        <SubscribeDialog
          enabled={calendarLinks}
          tz={tz}
          dateFormat={dateFormat}
          onClose={() => setSubscribing(false)}
        />
      )}
    </div>
  );
}

// An event as a row that opens it: Upcoming, and a day's list. Beside a date tile
// the time is what is left to say, so it is never the part that gets cut off
// (WS-16) — the line wraps instead. The type is in the row's words.
function EventRow({
  event,
  day,
  types,
  tz,
  onOpen,
}: {
  event: CalendarEvent;
  /** With a day, the row leads with its date tile and leaves the date out of the text. */
  day?: string;
  types: readonly EventType[];
  tz: string;
  onOpen: () => void;
}) {
  const color = typeColor(types, event.type);
  // A day's list stacks these 8px apart at 31px tall, so under a finger they
  // grow to 44 for real: invisible .hf-hit areas overlapped, and a tap a little
  // low opened the event below.
  const coarse = useMediaQuery('(pointer: coarse)');
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        ...rowLook,
        minHeight: coarse ? 44 : undefined,
        width: '100%',
        padding: 0,
        background: 'none',
        border: 0,
        textAlign: 'left',
        color: 'inherit',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {day ? (
        <DateTile
          day={day}
          look={{ background: tint(color, 10), border: `1px solid ${tint(color, 25)}` }}
        />
      ) : (
        <span style={{ ...swatch, ...eventLook(color) }} />
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--hf-text-md)',
            color: 'var(--hf-ink)',
            overflowWrap: 'anywhere',
          }}
        >
          {event.title}
        </span>
        <span style={rowMeta}>
          {eventTypeLabel(types, event.type)} · {whenText(event, tz, { omitDate: true })}
        </span>
      </span>
    </button>
  );
}

// A day, opened from its cell: everything on it, however many (the cell shows
// three and "+N more"; it used to hide the rest in a 132px scroller, WS-07), each
// event a way in to its detail, and for calendar:full a new event on that day.
function DayDialog({
  day,
  off,
  failed,
  holidays,
  events,
  types,
  tz,
  canPost,
  onClose,
  onOpenEvent,
  onNew,
}: {
  day: string;
  off: boolean;
  /** The month's read failed, so "nothing on this day" is not known. */
  failed: boolean;
  holidays: readonly Holiday[];
  events: readonly CalendarEvent[];
  types: readonly EventType[];
  tz: string;
  canPost: boolean;
  onClose: () => void;
  onOpenEvent: (e: CalendarEvent) => void;
  onNew: () => void;
}) {
  return (
    <Dialog
      open
      title={dayLabel(day, tz)}
      size="sm"
      onClose={onClose}
      footer={
        canPost && (
          <Button variant="primary" size="sm" icon="plus" onClick={onNew}>
            New event on this day
          </Button>
        )
      }
    >
      <div style={{ ...stack, gap: 'var(--hf-space-3)' }}>
        {off && <div style={{ ...note, color: 'var(--hf-ink-3)' }}>A non-working day.</div>}
        {failed ? (
          <InlineStatus tone="error">
            Couldn’t load this month, so this day may be missing events.
          </InlineStatus>
        ) : (
          !holidays.length && !events.length && <div style={note}>Nothing on this day.</div>
        )}
        {holidays.length > 0 && (
          <ul style={{ ...plainList, ...stack, gap: 'var(--hf-space-2)' }}>
            {holidays.map((h) => (
              <li key={h.id} style={rowLook}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--hf-text-md)' }}>
                  {h.name}
                </span>
                <span style={kindMark}>
                  <DayMark kind={h.kind} />
                </span>
              </li>
            ))}
          </ul>
        )}
        {events.length > 0 && (
          <ul style={{ ...plainList, ...stack, gap: 'var(--hf-space-2)' }}>
            {events.map((e) => (
              <li key={e.id}>
                <EventRow event={e} types={types} tz={tz} onOpen={() => onOpenEvent(e)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function EventDetail({
  event,
  types,
  tz,
  departments,
  canPost,
  google,
  onRetryGoogle,
  onClose,
  onEdit,
  onDeleted,
}: {
  event: CalendarEvent;
  types: readonly EventType[];
  tz: string;
  departments: readonly Department[];
  canPost: boolean;
  /** Shown to calendar:full. 'pending': its push to Google keeps failing (ticket
   *  05). 'unknown': the check itself failed. */
  google?: 'pending' | 'unknown';
  onRetryGoogle: () => void;
  onClose: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const color = typeColor(types, event.type);
  const loc = event.location?.trim();
  const isLink = !!loc && /^https?:\/\//i.test(loc);
  const audience =
    event.audience === 'department'
      ? event.department_ids
          .map((id) => departmentName(departments, id) ?? 'Unknown department')
          .join(', ')
      : AUDIENCE_LABEL[event.audience];
  const mine = canPost && event.source !== 'google';

  const remove = async () => {
    setConfirming(false);
    setBusy(true);
    setErr(null);
    const { error } = await deleteEvent(event.id);
    if (error) {
      setErr(error.message);
      setBusy(false);
      return;
    }
    onDeleted();
  };

  const line = (icon: IconName, children: ReactNode) => (
    <div style={{ display: 'flex', gap: 'var(--hf-space-2)', ...note }}>
      <Icon
        name={icon}
        size={14}
        style={{ flexShrink: 0, marginTop: 'var(--hf-space-1)', color: 'var(--hf-ink-3)' }}
      />
      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
  const aside: CSSProperties = { ...note, color: 'var(--hf-ink-3)' };

  return (
    <>
      <Dialog
        open
        title={event.title}
        size="sm"
        onClose={onClose}
        footer={
          mine && (
            <>
              <Button variant="danger" size="sm" loading={busy} onClick={() => setConfirming(true)}>
                Delete
              </Button>
              <Button size="sm" disabled={busy} onClick={onEdit}>
                Edit
              </Button>
            </>
          )
        }
      >
        <div style={{ ...stack, gap: 'var(--hf-space-2)' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-2)',
              marginBottom: 'var(--hf-space-1)',
            }}
          >
            <span style={{ ...swatch, ...eventLook(color) }} />
            <span>{eventTypeLabel(types, event.type)}</span>
          </div>
          {line(
            'calendar',
            <>
              {whenText(event, tz)}
              {!event.all_day && event.starts_at && (
                <div style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                  {tzLabel(tz, new Date(event.starts_at))}
                </div>
              )}
            </>,
          )}
          {/* A pin for a place, the chain only for something you can follow:
              "Studio 2" wore a link icon and went nowhere (WS-19). */}
          {loc &&
            line(
              isLink ? 'link' : 'pin',
              isLink ? (
                <a
                  href={loc}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--hf-accent-text)' }}
                >
                  {loc}
                </a>
              ) : (
                loc
              ),
            )}
          {line('users', audience)}
          {event.notes?.trim() && (
            <div
              style={{
                ...note,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                padding: 'var(--hf-space-2) var(--hf-space-3)',
                borderRadius: 'var(--hf-radius-md)',
                background: 'var(--hf-surface)',
                border: '1px solid var(--hf-rule)',
              }}
            >
              {event.notes}
            </div>
          )}
          {err && <InlineStatus tone="error">Couldn’t delete this event. {err}</InlineStatus>}
          {google === 'unknown' && (
            <LoadError what="whether this event is in Google yet" onRetry={onRetryGoogle} />
          )}
          {google === 'pending' && (
            <div style={aside}>
              <Badge tone="warning" dot>
                Not in Google yet
              </Badge>{' '}
              It saved here and its audience was told; copying it to the Hyprflow Google calendar is
              retried every 15 minutes (Google imports has the log).
            </div>
          )}
          {/* A Google event's title and time are Google's (the DB refuses an edit);
              calendar:full changes only its audience and type, in Google imports. */}
          {event.source === 'google' && (
            <div style={aside}>
              From the company Google Calendar. Its title and time change there.
              {canPost && (
                <>
                  {' '}
                  <Link
                    to="/workspace?tab=imports"
                    onClick={onClose}
                    style={{ color: 'var(--hf-accent-text)' }}
                  >
                    Change who sees it in Google imports
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>
      <ConfirmDialog
        open={confirming}
        title="Delete this event?"
        tone="danger"
        confirmLabel="Delete event"
        consequence={[
          `“${event.title}” comes off the calendar for everyone.`,
          'Everyone it was shared with is told it’s cancelled.',
          'It can’t be restored; you would post it again.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void remove()}
      />
    </>
  );
}

type FormErrors = Partial<Record<'title' | 'type' | 'starts' | 'ends' | 'departments', string>>;

const validate = (f: EventInput): FormErrors => {
  const e: FormErrors = {};
  if (!f.title.trim()) e.title = 'Give the event a title.';
  if (!f.type) e.type = 'Pick a type.';
  if (f.all_day) {
    if (!f.start_date) e.starts = 'Pick the day it starts.';
    else if (f.end_date && f.end_date < f.start_date) e.ends = 'The end can’t be before the start.';
  } else {
    if (!f.starts_local) e.starts = 'Pick when it starts.';
    if (!f.ends_local) e.ends = 'Pick when it ends.';
    else if (f.starts_local && f.ends_local < f.starts_local)
      e.ends = 'The end can’t be before the start.';
  }
  if (f.audience === 'department' && !f.department_ids.length)
    e.departments = 'Pick at least one department.';
  return e;
};

function EventForm({
  existing,
  day,
  types,
  tz,
  departments,
  onClose,
  onSaved,
}: {
  existing: CalendarEvent | null;
  /** A new event started from a day: that day is filled in, 09:00 to 10:00. */
  day: string | null;
  types: readonly EventType[];
  tz: string;
  departments: readonly Department[];
  onClose: () => void;
  onSaved: (title: string) => void;
}) {
  // An archived type is offered only to the event already filed under it.
  const pickable = pickableTypes(types, existing?.type);
  const pickableDepts = departments.filter(
    (d) => !d.archived || existing?.department_ids.includes(d.id),
  );
  const [initial] = useState<EventInput>(() => ({
    id: existing?.id ?? null,
    title: existing?.title ?? '',
    type: existing?.type ?? pickable[0]?.id ?? '',
    all_day: existing?.all_day ?? false,
    start_date: existing?.start_date ?? day ?? '',
    end_date: existing?.end_date ?? day ?? '',
    // The inputs hold the ORG's wall clock; the database applies tz_iana.
    starts_local: existing?.starts_at
      ? toOrgWallTime(existing.starts_at, tz)
      : day
        ? `${day}T09:00`
        : '',
    ends_local: existing?.ends_at ? toOrgWallTime(existing.ends_at, tz) : day ? `${day}T10:00` : '',
    location: existing?.location ?? '',
    notes: existing?.notes ?? '',
    audience: existing?.audience ?? 'company',
    department_ids: existing?.department_ids ?? [],
  }));
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  // Each message sits on the field it is about (Field.error: aria-invalid and
  // aria-describedby), not in one line above the buttons (WS-12, XA-10). Nothing
  // is flagged before the first Post/Save; after it the messages follow the
  // form, so a field stops reading invalid the moment it is put right.
  const [tried, setTried] = useState(false);
  const errors: FormErrors = tried ? validate(f) : {};
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const set = <K extends keyof EventInput>(k: K, v: EventInput[K]) =>
    setF((p) => ({ ...p, [k]: v }));
  // Moving the start past the end (or setting it first) drags the end along.
  const setStart = (k: 'start_date' | 'starts_local', end: 'end_date' | 'ends_local', v: string) =>
    setF((p) => ({ ...p, [k]: v, [end]: !p[end] || (p[end] ?? '') < v ? v : p[end] }));

  const submit = async () => {
    if (busy) return;
    setTried(true);
    setSaveErr(null);
    if (Object.keys(validate(f)).length) return;
    setBusy(true);
    const title = f.title.trim();
    const { error } = await saveEvent({
      ...f,
      title,
      end_date: f.end_date || f.start_date,
      location: f.location.trim(),
      notes: f.notes.trim(),
    });
    if (error) {
      setSaveErr(error.message);
      setBusy(false);
      return;
    }
    onSaved(title);
  };

  const pair: CSSProperties = { display: 'flex', gap: 'var(--hf-space-3)', flexWrap: 'wrap' };

  return (
    <Dialog
      open
      title={existing ? 'Edit event' : 'New event'}
      size="sm"
      onClose={onClose}
      // A typed event is not lost to Escape or a stray click on the backdrop.
      dirty={JSON.stringify(f) !== JSON.stringify(initial)}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" size="sm" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" loading={busy} onClick={() => void submit()}>
            {existing ? 'Save changes' : 'Post event'}
          </Button>
        </>
      )}
    >
      <div style={stack}>
        <Field label="Title" required error={errors.title}>
          <TextInput value={f.title} maxLength={200} onChange={(v) => set('title', v)} />
        </Field>

        <Field label="Type" error={errors.type}>
          {(control) => (
            <Select
              {...control}
              value={f.type}
              onChange={(v) => set('type', v)}
              options={pickable.map((t) => ({
                value: t.id,
                label: t.archived ? `${t.label} (archived)` : t.label,
              }))}
            />
          )}
        </Field>

        <div>
          <Checkbox label="All day" checked={f.all_day} onChange={(v) => set('all_day', v)} />
        </div>

        {f.all_day ? (
          <div style={pair}>
            {/* Each echoes its day in the org's format under the browser's
                order (UI-CONVENTIONS §2, Entering a date). */}
            <DateField
              label="Starts"
              required
              error={errors.starts}
              style={{ flex: '1 1 150px' }}
              value={f.start_date ?? ''}
              onChange={(v) => setStart('start_date', 'end_date', v)}
            />
            <DateField
              label="Ends"
              error={errors.ends}
              style={{ flex: '1 1 150px' }}
              value={f.end_date ?? ''}
              min={f.start_date ?? undefined}
              onChange={(v) => set('end_date', v)}
            />
          </div>
        ) : (
          <div>
            <div style={pair}>
              <Field label="Starts" required error={errors.starts} style={{ flex: '1 1 180px' }}>
                <TextInput
                  type="datetime-local"
                  mono
                  value={f.starts_local ?? ''}
                  onChange={(v) => setStart('starts_local', 'ends_local', v)}
                />
              </Field>
              <Field label="Ends" required error={errors.ends} style={{ flex: '1 1 180px' }}>
                <TextInput
                  type="datetime-local"
                  mono
                  value={f.ends_local ?? ''}
                  min={f.starts_local ?? undefined}
                  onChange={(v) => set('ends_local', v)}
                />
              </Field>
            </div>
            <div
              style={{
                marginTop: 'var(--hf-space-2)',
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-3)',
              }}
            >
              Times are in {tzLabel(tz)}, the company’s timezone.
            </div>
          </div>
        )}

        <Field label="Location or link">
          <TextInput value={f.location} onChange={(v) => set('location', v)} />
        </Field>

        <Field label="Notes">
          <TextArea value={f.notes} onChange={(v) => set('notes', v)} />
        </Field>

        <AudiencePicker
          audience={f.audience}
          departmentIds={f.department_ids}
          departments={pickableDepts}
          error={errors.departments}
          onChange={(audience, department_ids) => setF((x) => ({ ...x, audience, department_ids }))}
        />

        {/* In the dialog, not a toast: the page behind a modal is inert. */}
        {saveErr && (
          <InlineStatus tone="error">
            Couldn’t {existing ? 'save' : 'post'} this event. {saveErr}
          </InlineStatus>
        )}
      </div>
    </Dialog>
  );
}

// A personal ICS link (lib/calendarLinks.ts). Only its hash is stored, so the URL
// is shown once, right after it is made; after that the dialog can only say a
// link exists and offer Reset.
function SubscribeDialog({
  enabled,
  tz,
  dateFormat,
  onClose,
}: {
  enabled: boolean;
  tz: string;
  /** org_settings.date_format: the day the link was made is a full date (§2). */
  dateFormat: string;
  onClose: () => void;
}) {
  // undefined while asking; null when there is no link yet.
  const [made, setMade] = useState<string | null | undefined>(undefined);
  // The "do I have a link" read failed. `made` stays undefined, so neither
  // Create nor Reset is offered; Retry asks again without closing the dialog.
  const [checkFailed, setCheckFailed] = useState(false);
  const [checks, setChecks] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // null: not tried. The button's word alone told a screen reader nothing.
  const [copied, setCopied] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void linkCreatedAt().then(({ at, error }) => {
      if (!live) return;
      setCheckFailed(!!error);
      if (!error) setMade(at);
    });
    return () => {
      live = false;
    };
  }, [enabled, checks]);

  const reset = async () => {
    if (busy) return;
    setConfirming(false);
    setBusy(true);
    setErr(null);
    const res = await resetLink();
    setBusy(false);
    if (res.error || !res.url) {
      setErr(res.error?.message ?? 'Could not make a link.');
      return;
    }
    setUrl(res.url);
    setMade(new Date().toISOString());
    setCopied(null);
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard (an insecure origin): the field selects itself on focus.
      setCopied(false);
    }
  };

  return (
    <>
      <Dialog
        open
        title="Subscribe to your calendar"
        size="sm"
        onClose={onClose}
        // In the footer, not the body: on a phone the sheet keeps its footer in
        // reach while the instructions under it scroll (UI-CONVENTIONS §8).
        footer={
          enabled &&
          !url &&
          made !== undefined && (
            <Button
              size="sm"
              variant={made ? 'secondary' : 'primary'}
              loading={busy}
              // A first link replaces nothing; a reset kills the old one.
              onClick={() => (made ? setConfirming(true) : void reset())}
            >
              {made ? 'Reset link' : 'Create my link'}
            </Button>
          )
        }
      >
        {!enabled ? (
          <div style={note}>
            Subscription links are turned off for everyone. An owner or admin can turn them back on
            in Org Settings → Event types.
          </div>
        ) : (
          <div style={stack}>
            <div style={note}>
              See the events you can see here, plus holidays, in Google Calendar, Apple Calendar or
              Outlook. Notes stay in Hyprflow.
            </div>

            {url ? (
              <>
                <div style={{ display: 'flex', gap: 'var(--hf-space-2)', alignItems: 'flex-end' }}>
                  <Field label="Your link" style={{ flex: 1 }}>
                    <TextInput
                      readOnly
                      mono
                      value={url}
                      onChange={() => {}}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </Field>
                  <Button variant="primary" size="lg" onClick={() => void copy()}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                {copied !== null && (
                  <InlineStatus tone={copied ? 'success' : 'warning'}>
                    {copied
                      ? 'Link copied.'
                      : 'Couldn’t copy it for you. Select the link and copy it yourself.'}
                  </InlineStatus>
                )}
                <InlineStatus tone="warning">
                  This is the only time it’s shown — copy it now. Anyone with the link can see your
                  calendar, so keep it to yourself.
                </InlineStatus>
              </>
            ) : made === undefined ? (
              checkFailed ? (
                <LoadError
                  what="whether you already have a link"
                  onRetry={() => {
                    setCheckFailed(false);
                    setChecks((n) => n + 1);
                  }}
                />
              ) : (
                <div role="status" style={note}>
                  Checking…
                </div>
              )
            ) : made ? (
              <div style={note}>
                You made a link on {fmtDate(made, dateFormat, { tz })}. It can’t be shown again.
                Lost it, or shared it by mistake? Reset it — the old one stops working at once.
              </div>
            ) : null}

            {err && <InlineStatus tone="error">{err}</InlineStatus>}
            <div>
              <Eyebrow as="h3">How to add it</Eyebrow>
              <ul
                style={{
                  ...note,
                  margin: 'var(--hf-space-2) 0 0',
                  paddingLeft: 'var(--hf-space-4)',
                }}
              >
                <li>
                  <b>Google Calendar</b> (on the web): Other calendars → + → From URL → paste.
                  Google refreshes it every few hours, so changes arrive late there — the
                  notification here is instant.
                </li>
                <li>
                  <b>Apple Calendar</b>: File → New Calendar Subscription → paste.
                </li>
                <li>
                  <b>Outlook</b>: Add calendar → Subscribe from web → paste.
                </li>
              </ul>
            </div>
          </div>
        )}
      </Dialog>
      <ConfirmDialog
        open={confirming}
        title="Make a new link?"
        tone="danger"
        confirmLabel="Reset link"
        consequence={[
          'The old link stops working straight away, in every calendar app it was added to.',
          'The new one is shown once; you add it to each app again.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void reset()}
      />
    </>
  );
}

/** Who an event is for: the three audiences and, for Department(s), the
 *  department list. `departments` is what may be picked — the caller leaves
 *  archived ones out except those already chosen. Shared by the event form and
 *  Google imports. */
export function AudiencePicker({
  audience,
  departmentIds,
  departments,
  onChange,
  error,
}: {
  audience: Audience;
  departmentIds: readonly string[];
  departments: readonly Department[];
  onChange: (audience: Audience, departmentIds: string[]) => void;
  /** Shown under the department list it is about, and read as its description. */
  error?: string;
}) {
  const errorId = useId();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--hf-space-2)',
        minWidth: 0,
      }}
    >
      {/* The group's name comes from the control; this is its visible label. */}
      <Eyebrow as="div">
        <span aria-hidden="true">Audience</span>
      </Eyebrow>
      <SegmentedControl
        label="Audience"
        value={audience}
        options={(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => ({
          value: a,
          label: AUDIENCE_LABEL[a],
        }))}
        onChange={(a) => onChange(a, [...departmentIds])}
      />
      {audience === 'department' && (
        <div
          role="group"
          aria-label="Departments"
          aria-describedby={error ? errorId : undefined}
          style={{
            alignSelf: 'stretch',
            padding: 'var(--hf-space-2) var(--hf-space-3)',
            borderRadius: 'var(--hf-radius-md)',
            border: '1px solid var(--hf-rule-2)',
            maxHeight: 160,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--hf-space-1)',
          }}
        >
          {!departments.length && (
            <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
              No departments yet.
            </span>
          )}
          {departments.map((d) => (
            <Checkbox
              key={d.id}
              label={d.archived ? `${d.name} (archived)` : d.name}
              checked={departmentIds.includes(d.id)}
              onChange={(on) =>
                onChange(
                  audience,
                  on ? [...departmentIds, d.id] : departmentIds.filter((x) => x !== d.id),
                )
              }
            />
          ))}
        </div>
      )}
      {error && (
        <div
          id={errorId}
          role="alert"
          style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-rose-text)' }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
